import { Spacer, Text, Tooltip } from '@truepill/react-capsule'
import { isCashPaymentType } from '@truepill/tpos-data-util'
import { useQuery } from '@truepill/tpos-react-router'
import { FillStatus } from '@truepill/tpos-types'
import AccordionContainer from 'components/AccordionContainer'
import CriticalMedicalNoteWarning from 'components/CriticalMedicalNoteWarning'
import { MedicalNoteAlert } from 'components/CriticalMedicalNoteWarning/MedicalNoteAlert'
import DrugImageImprint from 'components/DrugImageImprint'
import FillRequestDetails from 'components/FillRequestDetails'
import { GET_NDC_PACKAGE } from 'components/ReviewPrescription'
import {
  ImageFilledHeader,
  ListRowLabel,
  ListRowValue,
  RightFilledHeader,
  RXCenterCell,
  RXCenterColumn,
  RXListRowTwoColumns,
  RXTitleRow,
  TableContainer,
} from 'components/RXTable'
import { Box } from 'grommet'
import useHotKey, { HotKeyLevel } from 'hooks/useHotKey'
import useRunClinicalReview from 'hooks/useRunClinicalReview'
import useRxImageIsPic from 'hooks/useRxImageIsPic'
import { AlertOctagon } from 'react-feather'
import styled from 'styled-components'
import { capsuleDarkBlue } from 'styles/styleVariables'
import type { Fill, NdcFullInfoPackage, Order, Prescription, RXFillRequest } from 'types'
import { getNdcText, getPackageMeasureDescription } from 'utils'
import { getFormattedInventoryGroupName } from 'utils/inventoryGroup'
import ReviewSafety from '../../../components/ReviewSafety'
import { DrugNameWithWarning, DrugNameTexts, DrugNameTags } from '../../fill/TableSections/Primary'

type MainPv2SectionProps = {
  fill: Fill
  prescription: Prescription
  order: Order
  rxFillRequest: RXFillRequest
}

const RobotImageSection = ({ rxFillRequest, css }: { rxFillRequest: RXFillRequest; css?: React.CSSProperties }) => {
  return (
    <>
      {rxFillRequest.machine?.vials && (
        <AccordionContainer css={css} title="Robot image">
          <Spacer desktop="sm" />
          {rxFillRequest.machine?.vials.map((vial, index) => (
            <>
              <VialHeader key={index}>Vial {vial.number}</VialHeader>
              <VialImageWrapper>
                <img alt={'drug'} src={`data:image/jpeg;base64, ${vial.image}`} />
              </VialImageWrapper>
            </>
          ))}
        </AccordionContainer>
      )}
    </>
  )
}

const NDCChangeTag = ({ ndc, medication }: { ndc: string; medication: string }) => {
  return (
    <WarningIconWrapper>
      <Tooltip
        css={{ boxShadow: '0px 6px 8px -6px #18274bbf' }}
        variant="white"
        label={
          <>
            <Text>
              <strong>NDC has changed from last fill</strong>
            </Text>
            <Text>{`Previous dispensed medication: ${medication} (${ndc})`}</Text>
          </>
        }
      >
        <AlertOctagon size="1rem" color={capsuleDarkBlue} />
      </Tooltip>
    </WarningIconWrapper>
  )
}

const MainPv2Section = ({ fill, order, prescription, rxFillRequest }: MainPv2SectionProps): JSX.Element => {
  const [rxImageIsPic, setRxImageIsPic] = useRxImageIsPic()

  const {
    runClinicalReview,
    loading: isClinicalReviewLoading,
    error: clinicalReviewError,
  } = useRunClinicalReview({
    onCompleted: () => {},
    onError: () => {},
  })

  const inventoryGroup = getFormattedInventoryGroupName(fill)

  const { patient } = prescription

  useHotKey('x', HotKeyLevel.normal, () => setRxImageIsPic(!rxImageIsPic))

  const lastFill = prescription.fills.filter(f => f.status === FillStatus.Complete).slice(-1)[0]
  const showNDCChangeTag = fill.verifications?.pv1?.skipped && lastFill?.dispensed.ndc !== fill.dispensed.ndc
  const showSameMedAuxLabelTag = fill.fillNumber > 0 && lastFill && lastFill.dispensed.ndc !== fill.dispensed.ndc

  const ndcs =
    !!fill?.dispensed.ndc && prescription.ndc !== fill.dispensed.ndc
      ? [prescription.ndc, fill.dispensed.ndc]
      : [prescription.ndc]

  const { data } = useQuery(GET_NDC_PACKAGE, {
    variables: { ndcs },
  })

  const ndcPackages = data?.getNdcPackage as NdcFullInfoPackage[]
  const dispensedNdcPackage = ndcPackages?.find(ndcPackage => ndcPackage?.ndc === fill?.dispensed?.ndc)

  const dispensedNdcText = getNdcText(dispensedNdcPackage)

  const manualEntryAtFill = Boolean(fill.dispensed.manualEntryFilled)

  return (
    <TableContainer>
      <RXTitleRow>
        <RXCenterColumn>
          <ImageFilledHeader>Dispensed medication</ImageFilledHeader>
        </RXCenterColumn>
        <RXCenterColumn>
          <RightFilledHeader>Fill request</RightFilledHeader>
        </RXCenterColumn>
      </RXTitleRow>
      <Box direction="row">
        <Box basis="1/3">
          <RXListRowTwoColumns hideImageColumn>
            <RXCenterCell>
              <ListRowLabel>Name:</ListRowLabel>
              <ListRowValue>{`${patient?.firstName} ${patient?.lastName}`}</ListRowValue>
            </RXCenterCell>
          </RXListRowTwoColumns>
          <RXListRowTwoColumns hideImageColumn>
            <RXCenterCell>
              <ListRowLabel>Medication:</ListRowLabel>
              <DrugNameWithWarning>
                <DrugNameTexts>
                  <span>{fill.dispensed.name}</span>
                  <span>{fill.dispensed.alternateLabelName}</span>
                </DrugNameTexts>
                <DrugNameTags>
                  {showSameMedAuxLabelTag && <MedicalNoteAlert note="NDC change aux label" variant="success" />}
                  <CriticalMedicalNoteWarning ndc={fill.dispensed.ndc} />
                </DrugNameTags>
              </DrugNameWithWarning>
            </RXCenterCell>
          </RXListRowTwoColumns>
          <RXListRowTwoColumns hideImageColumn>
            <RXCenterCell>
              <ListRowLabel>NDC:</ListRowLabel>
              <ListRowValue style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                {dispensedNdcText} {inventoryGroup}
                {manualEntryAtFill && <MedicalNoteAlert note="Captured by manual entry" />}
                {showNDCChangeTag && (
                  <NDCChangeTag medication={lastFill?.dispensed.name} ndc={lastFill?.dispensed.ndc} />
                )}
              </ListRowValue>
            </RXCenterCell>
          </RXListRowTwoColumns>
          <RXListRowTwoColumns hideImageColumn>
            <RXCenterCell>
              <ListRowLabel>Mfg:</ListRowLabel>
              <ListRowValue>{fill.dispensed.manufacturer}</ListRowValue>
            </RXCenterCell>
          </RXListRowTwoColumns>
          <RXListRowTwoColumns hideImageColumn>
            <RXCenterCell>
              <ListRowLabel>Qty:</ListRowLabel>
              <ListRowValue>
                {fill.dispensed.quantity} {getPackageMeasureDescription(fill.dispensed.packageSizeUnitOfMeasure)}
              </ListRowValue>
            </RXCenterCell>
          </RXListRowTwoColumns>
          <DrugImageImprint ndc={fill.dispensed.ndc} hasTwoColumns={true} hideImageColumn />
        </Box>
        <Box basis="1/3" overflow="auto">
          <FillRequestDetails
            fill={fill}
            order={order}
            prescription={prescription}
            rxFillRequest={rxFillRequest}
            showCopayCollected={!isCashPaymentType(rxFillRequest?.paymentType ?? '')}
          />
          <RobotImageSection rxFillRequest={rxFillRequest} css={{ marginLeft: '1.25rem' }} />
        </Box>
      </Box>
      <ReviewSafety
        order={order}
        fill={fill}
        rxFillRequest={rxFillRequest}
        prescription={prescription}
        isClinicalReviewLoading={isClinicalReviewLoading}
        clinicalReviewError={clinicalReviewError}
        onRerunClinicalReviewClick={() => {
          runClinicalReview({
            variables: {
              orderId: order._id,
              fillId: fill._id,
            },
          })
        }}
      />
    </TableContainer>
  )
}

const WarningIconWrapper = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
`

const VialHeader = styled(ListRowValue)`
  padding-left: 1.2rem;
  padding-top: 5px;
`

const VialImageWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  margin-top: 0.625rem;
  margin-bottom: 0.625rem;
  margin-left: 1.2rem;
  height: 300px;
  > img {
    height: 300px;
    width: 100%;
    margin: 5px;
    border-radius: 4px;
  }
`

export default MainPv2Section
